<template>
  <div class="basket mt-4">
    <client-only>
      <div class="page container">
        <b-row class="basket_container">
          <b-col lg="7">
            <div class="pt-4 pb-lg-4 px-2 d-flex flex-column">
              <div class="d-flex justify-content-between mb-2">
                <div class="mr-3">
                  <h1 class="basket__title m-0">Basket</h1>
                  <div class="basket__count m-0">
                    <p class="m-0">{{ countItems }} {{ countItemsLabel }}</p>
                  </div>
                </div>
                <DeliveryInfo
                  :delivery="orderDelivery"
                  :shipping-price="shippingPrice"
                  :region="region"
                  :region-delivery-limit="deliveryLimit"
                  :is-mixed-order="isMixedOrder"
                />
              </div>
              <div class="mb-3">
                <Divider />
              </div>

              <BasketProduct
                v-for="(product, index) in products"
                :key="product.id + index"
                class="mt-0 mb-3"
                :product="product"
                :index="index"
                @click="handleProductClick({ item: product })"
              />

              <div
                v-if="!countItems"
                class="d-flex flex-column align-items-center pb-4"
              >
                <h3 class="basket__title_empty">
                  You have 0 items in your basket
                </h3>
                <Button
                  id="Continue_shopping"
                  class="my-3"
                  full-width
                  @click="handleContinueShoppingClick"
                >
                  Continue Shopping
                </Button>
              </div>

              <div>
                <FeeDisclaimer />
              </div>
            </div>
          </b-col>
          <b-col lg="5">
            <div class="pb-4 pt-lg-4 px-2">
              <div class="summary d-flex flex-column">
                <PromoCodeInput
                  v-if="countItems"
                  v-model="discountModel"
                  :is-active="isEditDiscountActive"
                  :is-applied="isDiscountApplied"
                  @switchPromocodeInput="switchPromocodeInput"
                  @onApplyDiscount="onApplyDiscount"
                  @onRemoveDiscount="onRemoveDiscount"
                />
                <div class="py-2 px-3">
                  <div class="summary__title mb-2">Summary</div>
                  <div class="summary__pricing">
                    <span
                      >Subtotal
                      <span class="summary__pricing_hint"
                        >({{ countItems }} {{ countItemsLabel }})</span
                      ></span
                    >
                    <span>{{ formatMoney(subtotalPrice, currency) }}</span>
                  </div>
                  <div v-if="isDiscountApplied" class="summary__pricing">
                    <div>Promotion</div>
                    <div>- {{ formatMoney(discountCodePrice, currency) }}</div>
                  </div>
                  <div
                    v-if="orderDelivery === 'physical'"
                    class="summary__pricing"
                  >
                    <div>
                      Delivery
                      <span class="summary__pricing_hint"
                        >({{ deliveryLimit }})</span
                      >
                    </div>
                    <div>{{ formattedShippingPrice }}</div>
                  </div>
                  <div v-if="isTaxLabelVisible" class="summary__pricing">
                    <div>
                      Tax
                      <span class="summary__pricing_hint"
                        >(calculated at checkout)</span
                      >
                    </div>
                    <div>$ -</div>
                  </div>
                  <div class="summary__pricing summary__pricing-total">
                    <div>Total</div>
                    <div class="pricing__content">
                      <div>
                        {{ formatMoney(totalPrice, currency) }}
                      </div>
                      <div
                        v-if="isTaxLabelVisible"
                        class="pricing__content-hint"
                      >
                        Tax calculated at checkout
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="countItems"
                    class="summary__checkout-button_fixed mt-2"
                  >
                    <Button
                      id="Checkout_basket"
                      class="my-3"
                      full-width
                      :disabled="countEmpty || !countItems"
                      :loading="loadingToCheckout"
                      @click="handleProceedToCheckoutClick"
                    >
                      Go to checkout
                    </Button>

                    <div class="d-flex justify-content-center">
                      <payment-icons variant="basket" />
                    </div>
                  </div>

                  <StickyCheckoutSummary
                    v-if="countItems"
                    :is-tax-label-visible="isTaxLabelVisible"
                    :total="formatMoney(totalPrice, currency)"
                    :loading="loadingToCheckout"
                    @handleProceedToCheckoutClick="handleProceedToCheckoutClick"
                  />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <br v-if="isBannerEnabled" />
        <br v-if="isBannerEnabled" />

        <div v-if="isBannerEnabled" class="banner">
          <AdBanner type="basket" />
        </div>

        <Carousel
          id="also-like"
          class="basket-carousel my-5"
          :items="recommended"
          :mode="CarouselModes.PRODUCT"
          description-visible
          @click="handleProductClick"
        >
          <template #title> You may also like </template>
          <template #itemTitle="{ item }"> {{ item.name }} </template>
          <template #itemSubtitle="{ item }">
            {{ getProductFormatPrice(item) }}
          </template>
          <template #itemAction="{ item }">
            <Button
              id="Add_basket_carrousel"
              variant="outline"
              @click.stop="handleAddToBasketClick(item)"
            >
              Add to basket
            </Button>
          </template>
        </Carousel>
      </div>

      <Notification />
    </client-only>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import BasketProduct from '@/components/BasketProduct/index.vue';
import PromoCodeInput from '@/components/BasketProduct/PromoCodeInput.vue';
import StickyCheckoutSummary from '@/components/BasketProduct/StickyCheckoutSummary.vue';
import DeliveryInfo from '@/components/BasketProduct/DeliveryInfo.vue';
import FeeDisclaimer from '@/components/FeeDisclaimer.vue';
import Button from '@/components/Elements/Button';
import Divider from '@/components/Elements/Divider';
import Notification from '@/components/Notification';

import { CarouselModes } from '@/misc/constants';
import { fetchProducts } from '@/services/products';
import formatMoney from '@/misc/formatMoney';
import getDeliveryLimitDescription from '@/misc/getDeliveryLimitDescription';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import getFormatPrice from '@/misc/getFormatPrice';
import filterProductsByRegion from '@/misc/filterProductByRegion';
import notificationMixin from '~/checkoutForm/lib/notificationMixin';
import { trackViewBasketEvent } from '@/lib/analytics';
import AdBanner from '@/components/AdBanner/index.vue';
import PaymentIcons from '@/components/PaymentIcons/PaymentIcons.vue';

export default {
  name: 'BasketPage',

  components: {
    AdBanner,
    Button,
    Divider,
    BasketProduct,
    FeeDisclaimer,
    Notification,
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index'),
    PaymentIcons,
    PromoCodeInput,
    DeliveryInfo,
    StickyCheckoutSummary
  },

  mixins: [notificationMixin],

  data() {
    return {
      discountModel: '',
      isDiscountApplied: false,
      loadingToCheckout: false,
      showDiscountEdit: true,
      isEditDiscountActive: false,
      recommended: [],
      CarouselModes
    };
  },

  head: {
    title: 'Shopping Cart | Found',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: 'Sadly your basket is empty'
      }
    ]
  },

  computed: {
    ...mapGetters('basket', ['discount']),
    ...mapState('checkout', ['products']),
    ...mapGetters({
      redirectUrl: 'checkout/redirectUrl',
      currency: 'config/currency',
      countItems: 'checkout/countItems',
      shippingRate: 'shipping-method/shippingRate',
      shippingMethod: 'shipping-method/shippingMethod',
      region: 'config/region',
      isBannerEnabled: 'config/isBannerEnabled'
    }),
    subtotalWithDiscount() {
      if (!(this.discount && this.discount.percentOff)) {
        return this.subtotalPrice;
      }
      return this.products?.reduce((sum, item) => {
        const format = item?.formats?.find(
          (format) => format._id === item.productFormatId
        );
        const { amount } = getFormatPrice(format, this.region);
        // if coupon is "special" and apply only to some products
        if (this.discount.productIds.length) {
          const itemPrice = this.discount.productIds.includes(item._id)
            ? amount - (amount * this.discount.percentOff) / 100
            : amount;
          return sum + item.quantity * itemPrice;
        } else {
          // if coupon is default
          const itemPrice = amount - (amount * this.discount.percentOff) / 100;
          return sum + item.quantity * itemPrice;
        }
      }, 0);
    },
    totalPrice() {
      if (!this.countItems) {
        return 0;
      }

      return this.subtotalWithDiscount + this.shippingPrice;
    },
    discountCodePrice() {
      if (!(this.discount && this.discount.percentOff)) {
        return this.subtotalPrice;
      }
      return this.subtotalPrice - this.subtotalWithDiscount;
    },
    countEmpty() {
      return this.products.some((product) => !product.quantity);
    },
    subtotalPrice() {
      return this.products?.reduce((sum, item) => {
        const format = item?.formats?.find(
          (format) => format._id === item.productFormatId
        );
        const { amount } = getFormatPrice(format, this.region);
        return sum + item.quantity * amount;
      }, 0);
    },
    shippingPrice() {
      if (this.orderDelivery === 'digital') {
        return 0;
      }

      return this.shippingRate?.amount || 0;
    },
    formattedShippingPrice() {
      if (this.shippingPrice !== 0 && !this.countItems) {
        return formatMoney(0, this.currency);
      }

      return this.shippingPrice === 0
        ? 'FREE'
        : formatMoney(this.shippingPrice, this.currency);
    },
    isTaxLabelVisible() {
      const hasExclusiveProducts = this.products.some((product) => {
        const format = product.formats?.find(
          ({ _id }) => _id === product.productFormatId
        );
        const price = getFormatPrice(format, this.region);

        return price.taxBehaviour === 'exclusive';
      });

      return (
        hasExclusiveProducts || this.shippingRate?.taxBehaviour === 'exclusive'
      );
    },
    countItemsLabel() {
      return this.countItems === 1 ? 'item' : 'items';
    },
    productFulfilments() {
      return this.products.map((product) => {
        const format = product.formats.find(
          (format) => format._id === product.productFormatId
        );
        return format.fulfilment;
      });
    },
    isMixedOrder() {
      const hasDigital = this.productFulfilments.includes('Digital File');

      const hasOtherValues = this.productFulfilments.some(
        (value) => value !== 'Digital File'
      );

      return hasDigital && hasOtherValues;
    },
    orderDelivery() {
      if (this.region === 'GO') {
        return 'digital';
      }
      if (!this.products.length) {
        return 'physical'; // by default
      }

      const productFormatFulfilments = this.productFulfilments;

      const eachIsDigital = productFormatFulfilments.every(
        (fulfilment) => fulfilment === 'Digital File'
      );

      return eachIsDigital ? 'digital' : 'physical';
    },
    deliveryLimit() {
      if (this.orderDelivery === 'digital') {
        return 'Download emailed within 2 hours';
      }

      return getDeliveryLimitDescription(this.shippingRate);
    }
  },

  watch: {
    region: {
      immediate: true,
      async handler(value) {
        if (value) {
          await this.fetchActiveRegionShippingMethods();
        }
      }
    }
  },

  mounted() {
    this.showNotification({
      group: 'success',
      title: this.$t('Promo code successfully applied, your discount is '),
      type: 'success'
    });
    this.$notify({
      group: 'success',
      title: this.$t('Promo code successfully applied, your discount is '),
      type: 'success',
      duration: 3000,
      position: 'bottom center'
    });
    fetchProducts(this.$axios, { query: { $limit: 0 } })
      .then((products) => {
        this.recommended = filterProductsByRegion(products?.data, this.region);

        this.discountModel =
          this.discount?.code && !this.discount?.message
            ? this.discount?.code
            : '';

        if (this.discountModel) {
          this.isDiscountApplied = true;
        }
      })
      .catch((err) => {
        this.$showToastMessage(
          this.$get(err, 'response.data.message'),
          'Error',
          'danger'
        );
      });

    trackViewBasketEvent({ cart: this.products });
  },

  methods: {
    ...mapMutations({
      ADD_ITEM: 'checkout/ADD_ITEM',
      SET_DISCOUNT_DATA: 'basket/SET_DISCOUNT_DATA'
    }),
    ...mapActions({
      createSession: 'checkout/createSession',
      fetchActiveRegionShippingMethods:
        'shipping-method/fetchActiveRegionShippingMethods'
    }),
    ...mapActions('basket', ['applyDiscount']),
    formatMoney,
    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },
    handleProceedToCheckoutClick() {
      this.loadingToCheckout = true;
      this.createSession(this.isDiscountApplied ? this.discountModel : '')
        .then(() => {
          window.location.href = this.redirectUrl;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            'danger'
          );
        })
        .finally(() => {
          this.loadingToCheckout = false;
        });
    },
    handleProductClick(params) {
      this.$router.push({ path: `/products/${params?.item?.id}` });
    },
    handleAddToBasketClick(item) {
      this.ADD_ITEM({
        product: item,
        discounted: true,
        productFormatId: getDefaultProductFormat(item)?._id
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    },
    handleContinueShoppingClick() {
      this.$router.push({ path: '/cookbooks' });
    },
    async onApplyDiscount() {
      if (this.discountModel.trim().length === 0) return;

      await this.applyDiscount(this.discountModel);

      if (this.discount.percentOff) {
        this.$showToastMessage(
          this.$t(
            'Promo code successfully applied, your discount is ' +
              this.discount.percentOff +
              ' percent'
          ),
          'Promo code',
          'success'
        );

        this.isDiscountApplied = true;
        this.isEditDiscountActive = false;

        trackViewBasketEvent({ cart: this.products });
      } else {
        const errorMessage = `Promocode ${this.discountModel} is not valid`;
        this.$showToastMessage(errorMessage, 'Error', 'danger');
      }
    },
    onRemoveDiscount() {
      this.SET_DISCOUNT_DATA(null);
      this.isDiscountApplied = false;
      this.isEditDiscountActive = false;
      this.discountModel = '';
    },
    switchPromocodeInput() {
      this.isEditDiscountActive = !this.isEditDiscountActive;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../checkoutForm/styles/variables.scss';

.basket {
  .page {
    font-family: 'Poppins-Regular', sans-serif;

    .basket_container {
      color: $dark-color;
      background: $white-color;

      .basket__count {
        font-size: 14px;
        font-weight: $font-weight-regular;
      }
    }
  }
  .basket__title {
    font-size: 32px;
    line-height: 32px;
    font-weight: $font-weight-demi-bold;
    font-family: 'Poppins-Regular', sans-serif;

    &_empty {
      font-size: 24px;
      font-weight: $font-weight-demi-bold;
      margin: 40px 0;
      text-align: center;
      font-family: 'Poppins-SemiBold', sans-serif;
    }
  }

  &__payment-icons {
    display: flex;
    margin-bottom: 70px;
  }
}
.summary {
  &__title {
    font-size: 20px;
    font-weight: $font-weight-demi-bold;

    @media (max-width: $screen-width-lg) {
      font-size: 24px;
    }
  }
  &__pricing {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 16px;

    @media (max-width: $screen-width-lg) {
      font-size: 18px;
    }

    &_hint {
      font-size: 13px;
    }

    &-total {
      font-weight: $font-weight-demi-bold;
      font-size: 20px;
      padding: 16px 0;
      border-top: 1px solid #ccc;
      border-bottom: 1px solid #ccc;

      @media (max-width: $screen-width-lg) {
        font-size: 24px;
      }
    }
  }
  &__checkout {
    &-button_fixed {
      display: flex;
      flex-direction: column;

      @media (max-width: $screen-width-lg) {
        display: none;
      }
    }
  }
}

.pricing {
  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    &-hint {
      font-size: 13px;
      color: $grey-color;
      font-weight: $font-weight-regular;
    }
  }
}

.christmas-wrapper {
  display: flex;
  justify-content: space-between;
  .left-wrapper {
    width: 45%;
  }
  @media (max-width: $screen-width-lg) {
    flex-direction: column;
    .left-wrapper {
      width: 100%;
    }
  }
}
.banner {
  width: auto;

  @media (min-width: $screen-width-sm) {
    margin-right: -15px;
    margin-left: -15px;
  }
}
.basket-carousel {
  @media (min-width: $screen-width-sm) {
    margin-right: -15px;
    margin-left: -15px;
  }
}
.btn-long {
  width: 100%;
  flex: 0 0 100%;
  max-width: 100%;
}
</style>
